import { createI18n } from 'vue-i18n';
import { getCurrentLanguage } from '@eureka/language-tool';
import _ from 'lodash';
import { inputUniversalLocale } from '@/components/air/search-inputs/src/input-universal/locales';
import { create } from '@eureka/utils/deferred';
import cn from './cn.json';
import en from './en.json';
import jp from './jp.json';
import tw from './tw.json';

// i18n 加载状态管理
export const i18nLoadedDeferred = create<boolean>();
let isI18nLoading = false;
let isI18nLoaded = false;

/**
 * 创建 i18n 实例
 * 使用空占位符作为 missing handler,防止在语言包加载前显示 key
 */
export function createI18nInstance(locale?: string) {
    locale = locale || getCurrentLanguage()
    return createI18n({
        legacy: false,
        locale,
        fallbackLocale: 'en',
        messages: {
            ...inputUniversalLocale,
            cn,
            en,
            jp,
            tw,
        },
        // 关键配置: 当 key 未找到时返回空字符串而不是 key 本身
        missing: (locale, key) => {
            console.debug(`[I18n] Missing translation for key: ${key} (locale: ${locale}), i18n loaded: ${isI18nLoaded}`);
            // 语言包未加载完成时返回空字符串,避免显示 key
            // 语言包加载完成后,如果还缺失 key,也返回空字符串(或者可以返回 key 用于调试)
            return '';
        },
        // 静默警告,避免控制台大量 missing key 警告
        silentTranslationWarn: true,
        // 在开发环境可以开启,用于调试缺失的翻译
        missingWarn: process.env.NODE_ENV === 'development',
        fallbackWarn: process.env.NODE_ENV === 'development',
    })
}

export const i18n = createI18nInstance()

/**
 * 懒加载 i18n 语言包
 * 这个函数会在后台异步加载,不阻塞应用挂载
 */
export async function loadI18n() {
    // 防止重复加载
    if (isI18nLoaded) {
        console.debug('[I18n] Already loaded, skipping');
        return i18n;
    }

    if (isI18nLoading) {
        console.debug('[I18n] Already loading, waiting for completion');
        await i18nLoadedDeferred.promise;
        return i18n;
    }

    isI18nLoading = true;
    console.debug('[I18n] Loading started');
    const loadStartTime = performance.now();

    try {
        const lang = getCurrentLanguage()
        let results;

        if (lang === 'cn') {
            results = await Promise.all([
                import('CommonMessages/eureka-common-product/cn.json'),
                import('CommonMessages/eureka-common-errorcode/cn.json'),
                import('CommonMessages/eureka-air/cn.json'),
                import ('element-plus/es/locale/lang/zh-cn')
            ])
        } else if (lang === 'en') {
            results = await Promise.all([
                import('CommonMessages/eureka-common-product/en.json'),
                import('CommonMessages/eureka-common-errorcode/en.json'),
                import('CommonMessages/eureka-air/en.json'),
                import ('element-plus/es/locale/lang/en')
            ])
        } else if (lang === 'jp') {
            results = await Promise.all([
                import('CommonMessages/eureka-common-product/jp.json'),
                import('CommonMessages/eureka-common-errorcode/jp.json'),
                import('CommonMessages/eureka-air/jp.json'),
                import ('element-plus/es/locale/lang/ja')
            ])
        } else if (lang === 'tw') {
            results = await Promise.all([
                import('CommonMessages/eureka-common-product/tw.json'),
                import('CommonMessages/eureka-common-errorcode/tw.json'),
                import('CommonMessages/eureka-air/tw.json'),
                import ('element-plus/es/locale/lang/zh-tw')
            ])
        }

        const data = results?.reduce((obj, { default: data }) => {
            return _.merge(obj, data);
        }, {});

        if (data) {
            i18n.global.mergeLocaleMessage(lang, data);
        }

        isI18nLoaded = true;
        i18nLoadedDeferred.resolve(true);

        const loadDuration = performance.now() - loadStartTime;
        console.debug(`[I18n] Loading completed in ${loadDuration.toFixed(2)}ms`);

        return i18n;
    } catch (error) {
        console.error('[I18n] Loading failed:', error);
        isI18nLoading = false;
        i18nLoadedDeferred.reject(error);
        throw error;
    }
}

/**
 * 获取 i18n 加载状态
 */
export function isI18nReady(): boolean {
    return isI18nLoaded;
}
